import { queryOptions } from '@tanstack/react-query';
import type { GetAllQuery } from '@utilities-one/reipm-api-sdk';
import type { InferResponseType } from 'hono/client';

import type { AgGridQueryOptions } from '@/components/grid/agGridTableReducer';
import { api, parseResponse } from '@/library/api/honoClient';

/**
 * UG-4500 — user entity read layer, replacing the legacy `'use server'`
 * actions (`entities/user/actions/*`) with TanStack Query over the Hono RPC
 * client.
 *
 * Types are the WIRE shapes (`InferResponseType`): `Date` columns arrive as
 * ISO strings and stay strings — every formatter in `@/library/lang/date`
 * accepts `Date | string`, so nothing here needs `parseResponseWithDates`
 * (which would make the static types lie about runtime `Date` instances).
 */

/** One row of the users grid (`allClients` is precomputed server-side). */
export type UserRow = InferResponseType<typeof api.users.getAll.$post>[number];

/** Full detail shape (relations, skills, comments) for `/users/:id/overall`. */
export type UserDetails = InferResponseType<typeof api.users.getUserDetails.$post>;

/** `{ id, name }`-ish option rows returned by the scoped picker endpoints. */
export type UserPickerOption = InferResponseType<typeof api.users.getTechnicians.$post>[number];

export const userQueryKeys = {
  /** Root key — also the prefix of the grid's `['users', 'grid', …]` keys, so invalidating it refreshes the grid. */
  all: ['users'] as const,
  details: (id: string) => [...userQueryKeys.all, 'details', id] as const,
  picker: (name: string, ...args: string[]) => [...userQueryKeys.all, 'picker', name, ...args] as const,
};

/**
 * Unified rows+total query for one users-grid page (the provider grid's
 * `query` contract — gpm's `TableQueryTQUnified` shape): `getAll` and
 * `getTotal` share the same filter, fetched in parallel.
 */
export const userListQuery = (query: GetAllQuery): AgGridQueryOptions<UserRow> => ({
  queryKey: [...userQueryKeys.all, 'grid', query] as const,
  queryFn: async (): Promise<[UserRow[], number]> => {
    const [rows, { total }] = await Promise.all([
      parseResponse(api.users.getAll.$post({ json: query })),
      parseResponse(api.users.getTotal.$post({ json: { filter: query.filter } })),
    ]);
    return [rows, total];
  },
});

export const userDetailsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.details(id),
    queryFn: () => parseResponse(api.users.getUserDetails.$post({ json: { id } })),
  });

/* ---------------------------------------------------------------------------
 * Scoped pickers (legacy `TechnicianAutocomplete` / `SupervisorsAutocomplete` /
 * `ClientUserAutocomplete` / … data sources). Consumed as
 * `useQuery(techniciansQueryOptions())` + mapped to `{ label, value }` options
 * for `field.Autocomplete` / `field.MultiSelect`.
 * ------------------------------------------------------------------------- */

/** Technicians (and supervisor-technicians) visible to the current user. */
export const techniciansQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.picker('technicians'),
    queryFn: () => parseResponse(api.users.getTechnicians.$post()),
  });

/** Every technician regardless of the caller's market scoping. */
export const allTechniciansQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.picker('allTechnicians'),
    queryFn: () => parseResponse(api.users.getAllTechnicians.$post()),
  });

export const techniciansByClientQueryOptions = (clientId: string) =>
  queryOptions({
    queryKey: userQueryKeys.picker('techniciansByClient', clientId),
    queryFn: () => parseResponse(api.users.getTechniciansByClient.$post({ json: { clientId } })),
    enabled: clientId.length > 0,
  });

export const supervisorsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.picker('supervisors'),
    queryFn: () => parseResponse(api.users.getSupervisors.$post()),
  });

/**
 * Active Admins who may own a market: Multi-Market admins, admins with no
 * market and, when `marketId` is given, the admins already in that market.
 */
export const ownerCandidatesQueryOptions = (marketId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.picker('ownerCandidates', marketId ?? ''),
    queryFn: () => parseResponse(api.users.getOwnerCandidates.$post({ json: { marketId } })),
  });

/** Users with the `client` role (legacy `ClientUserAutocomplete`). */
export const clientUsersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.picker('clientUsers'),
    queryFn: () => parseResponse(api.users.getAllClientUsers.$post()),
  });

export const availableSupervisorTechniciansQueryOptions = (clientId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.picker('availableSupervisorTechnicians', clientId ?? ''),
    queryFn: () => parseResponse(api.users.getAvailableSupervisorTechnicians.$post({ json: { clientId } })),
  });

export const usersByClientIdQueryOptions = (clientId: string) =>
  queryOptions({
    queryKey: userQueryKeys.picker('byClientId', clientId),
    queryFn: () => parseResponse(api.users.getAllByClientId.$post({ json: { clientId } })),
    enabled: clientId.length > 0,
  });

/** Every user, id+name only (legacy `AllUsersAutocomplete`). */
export const usersForAutocompleteQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.picker('forAutocomplete'),
    queryFn: () => parseResponse(api.users.getAllForAutocomplete.$post()),
  });

/** Map picker rows to the form-kit's `{ label, value }` option shape. */
export const toUserOptions = (users: { id: string; name: string }[] | undefined) =>
  (users ?? []).map(({ id, name }) => ({ value: id, label: name }));
